import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  RecaptchaVerifier, 
  signInWithPhoneNumber, 
  PhoneAuthProvider, 
  signOut,
  Auth,
  ConfirmationResult,
  User 
} from 'firebase/auth';
import { env } from './env';

// Firebase configuration
const firebaseConfig = {
  apiKey: env.firebase.apiKey,
  authDomain: env.firebase.authDomain,
  projectId: env.firebase.projectId,
  storageBucket: env.firebase.storageBucket,
  messagingSenderId: env.firebase.messagingSenderId,
  appId: env.firebase.appId
};

// Initialize Firebase
let app;
let auth: Auth;

try {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  console.log('Firebase initialized successfully');
} catch (error) {
  console.error('Error initializing Firebase:', error);
}

// Helper function to setup recaptcha
export const setupRecaptcha = (containerId: string): RecaptchaVerifier => {
  if (!auth) {
    throw new Error('Firebase authentication is not initialized');
  }
  
  try {
    return new RecaptchaVerifier(auth, containerId, {
      size: 'invisible',
      callback: () => {
        // reCAPTCHA solved, allow sign in
      }
    });
  } catch (error) {
    console.error('Error setting up reCAPTCHA:', error);
    throw new Error('Failed to set up reCAPTCHA verification');
  }
};

// Send OTP
export const sendOTP = async (phoneNumber: string, recaptchaVerifier: RecaptchaVerifier): Promise<ConfirmationResult> => {
  if (!auth) {
    throw new Error('Firebase authentication is not initialized');
  }
  
  try {
    const formattedPhone = phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`;
    const confirmationResult = await signInWithPhoneNumber(auth, formattedPhone, recaptchaVerifier);
    return confirmationResult;
  } catch (error: any) {
    console.error('Error sending OTP:', error);
    
    if (error.code === 'auth/invalid-phone-number') {
      throw new Error('The phone number format is incorrect. Please enter a valid phone number.');
    } else if (error.code === 'auth/too-many-requests') {
      throw new Error('Too many requests. Please try again later.');
    } else {
      throw error;
    }
  }
};

// Verify OTP
export const verifyOTP = async (confirmationResult: ConfirmationResult, otp: string) => {
  try {
    return await confirmationResult.confirm(otp);
  } catch (error: any) {
    console.error('Error verifying OTP:', error);
    
    if (error.code === 'auth/invalid-verification-code') {
      throw new Error('The verification code is invalid. Please try again.');
    } else {
      throw error;
    }
  }
};

// Sign out
export const signOutUser = async (): Promise<void> => {
  if (!auth) {
    throw new Error('Firebase authentication is not initialized');
  }
  
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

// Get current user
export const getCurrentUser = (): User | null => {
  if (!auth) {
    console.error('Firebase authentication is not initialized');
    return null;
  }
  return auth.currentUser;
};

// Listen to auth state changes
export const onAuthStateChanged = (callback: (user: User | null) => void): (() => void) => {
  if (!auth) {
    console.error('Firebase authentication is not initialized');
    return () => {}; // Return empty unsubscribe function
  }
  return auth.onAuthStateChanged(callback);
};